import React, { useEffect, useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { createPortal } from 'react-dom';

interface ToastProps {
  message: string;
  subMessage?: string;
  icon: IconDefinition;
  iconBgColor?: string;
  iconClassName?: string;
  duration?: number;
  onClose: () => void;
  id: string;
}

interface ToastContainerProps {
  position?: 'top-right' | 'bottom-right';
}

const Toast: React.FC<ToastProps> = ({
  message,
  subMessage,
  icon,
  iconBgColor = 'bg-green-400',
  iconClassName = '',
  duration = 3000,
  onClose,
  id,
}) => {
  const [mounted, setMounted] = useState(false);
  const [exiting, setExiting] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const exitTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle mounting animation
  useEffect(() => {
    // Start entrance animation
    const mountTimer = setTimeout(() => {
      setMounted(true);
    }, 10); // Small delay to ensure CSS transition works

    // Set exit timer
    timeoutRef.current = setTimeout(() => {
      startExit();
    }, duration);

    return () => {
      clearTimeout(mountTimer);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (exitTimeoutRef.current) clearTimeout(exitTimeoutRef.current);
    };
  }, [duration]);

  const startExit = () => {
    setExiting(true);

    // After exit animation completes, call onClose
    exitTimeoutRef.current = setTimeout(() => {
      onClose();
    }, 300); // Duration of exit transition
  };

  // Base classes for toast - removed "fixed" class
  const baseClasses =
    'transition-all duration-300 ease-out flex w-[350px] items-center gap-x-2 rounded-lg p-4 text-white bg-primary shadow-xl';

  // Position and opacity based on state
  const stateClasses = exiting ? 'translate-y-full opacity-0' : mounted ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0';

  return (
    <div
      id={id}
      className={`${baseClasses} ${stateClasses}`}
      style={{
        visibility: exiting && !mounted ? 'hidden' : 'visible',
      }}
    >
      <div className='shrink-0'>
        <FontAwesomeIcon icon={icon} className={`size-6 rounded-full ${iconBgColor} p-2 ${iconClassName}`} />
      </div>
      <div className='flex flex-col overflow-hidden'>
        <div className='truncate text-lg font-bold'>{message}</div>
        {subMessage && <div className='text-sm'>{subMessage}</div>}
      </div>
    </div>
  );
};

export interface ToastOptions {
  message: string;
  subMessage?: string;
  icon: IconDefinition;
  iconBgColor?: string;
  iconClassName?: string;
  duration?: number;
  id?: string;
}

class ToastManager {
  private static instance: ToastManager;
  private listeners: ((toasts: ToastOptions[]) => void)[] = [];
  private toasts: ToastOptions[] = [];
  private counter = 0;

  static getInstance(): ToastManager {
    if (!ToastManager.instance) {
      ToastManager.instance = new ToastManager();
    }
    return ToastManager.instance;
  }

  addToast(options: ToastOptions) {
    const toast = {
      ...options,
      id: `toast-${this.counter++}`,
      duration: options.duration || 3000,
    };

    // Add to beginning of array (newest first)
    this.toasts.unshift(toast);
    this.notifyListeners();
  }

  removeToast(toastId: string) {
    // Remove toast from array
    this.toasts = this.toasts.filter((t) => t.id !== toastId);
    this.notifyListeners();
  }

  subscribe(listener: (toasts: ToastOptions[]) => void) {
    this.listeners.push(listener);
    // Initial call
    listener([...this.toasts]);

    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  private notifyListeners() {
    this.listeners.forEach((listener) => listener([...this.toasts]));
  }
}

export const toastManager = ToastManager.getInstance();

export const showToast = (options: ToastOptions) => {
  toastManager.addToast(options);
};

export const ToastContainer: React.FC<ToastContainerProps> = ({ position = 'bottom-right' }) => {
  const [toasts, setToasts] = useState<ToastOptions[]>([]);

  useEffect(() => {
    const unsubscribe = toastManager.subscribe(setToasts);
    return unsubscribe;
  }, []);

  // Fixed positioning container
  const positionClass = 'fixed top-4 right-4 z-50 flex flex-col-reverse gap-2 pointer-events-none';

  return createPortal(
    <div className={positionClass}>
      {toasts.map((toast) => (
        <div key={toast.id} className='pointer-events-auto'>
          <Toast key={toast.id} id={toast.id || ''} {...toast} onClose={() => toastManager.removeToast(toast.id || '')} />
        </div>
      ))}
    </div>,
    document.body,
  );
};

// These are kept to ensure compatibility with tailwind.config.js but we're
// now using CSS transitions instead of animations
const slideInAnimation = {
  'slide-in': {
    '0%': {
      transform: 'translateY(100%)',
      opacity: '0',
    },
    '100%': {
      transform: 'translateY(0)',
      opacity: '1',
    },
  },
};

const slideOutAnimation = {
  'slide-out': {
    '0%': {
      transform: 'translateY(0)',
      opacity: '1',
    },
    '100%': {
      transform: 'translateY(100%)',
      opacity: '0',
    },
  },
};

export const animations = {
  ...slideInAnimation,
  ...slideOutAnimation,
};
